import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Users as UsersIcon, ShoppingBag, Clock, ChevronRight, Package, Map, FileText } from "lucide-react";

function StatCard({ color, icon: Icon, value, label, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl p-6 flex items-center gap-5 text-white shadow-md" style={{ backgroundColor: color }}>
      <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
        <Icon className="w-7 h-7" />
      </div>
      <div>
        <div className="text-4xl font-bold leading-none">{value}</div>
        <div className="text-sm mt-1.5 opacity-90">{label}</div>
      </div>
    </div>
  );
}

function ShortcutCard({ icon: Icon, title, subtitle, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="group text-left bg-white rounded-2xl p-6 border border-[#ecdfc4] shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-[#FBF0DC] flex items-center justify-center group-hover:bg-[#8B4513] transition-colors">
          <Icon className="w-6 h-6 text-[#8B4513] group-hover:text-white transition-colors" />
        </div>
        <ChevronRight className="w-5 h-5 text-[#c3b294] group-hover:text-[#8B4513] transition-colors" />
      </div>
      <div className="text-lg font-bold text-[#5A3D1F]">{title}</div>
      <div className="text-sm text-[#8A7A68] mt-1">{subtitle}</div>
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => toast.error("Failed to load stats"));
  }, []);

  return (
    <div className="animate-fade-up">
      <h1 className="text-2xl font-bold text-[#5A3D1F]">Operations Overview</h1>
      <p className="text-[#8A7A68] mt-1 mb-6">Live snapshot from the Divine Cakery database</p>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-testid="stat-cards">
        <StatCard color="#4CAF50" icon={UsersIcon} value={stats?.total_users ?? "–"} label="Total Customers" testid="stat-customers" />
        <StatCard color="#2196F3" icon={ShoppingBag} value={stats?.total_products ?? "–"} label="Products" testid="stat-products" />
        <StatCard color="#f44336" icon={Clock} value={stats?.pending_orders ?? "–"} label="Pending Orders" testid="stat-pending" />
      </div>

      {/* Shortcuts to the 3 working pages */}
      <h2 className="text-lg font-bold text-[#8B4513] mb-4">Quick Access</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ShortcutCard icon={Package} title="Manage Stock" subtitle="Update closing stock in grid or list view" onClick={() => navigate("/stock")} testid="shortcut-stock" />
        <ShortcutCard icon={Map} title="Route Summaries" subtitle="Delivery pivot by trip, shortages & printing" onClick={() => navigate("/route-summaries")} testid="shortcut-routes" />
        <ShortcutCard icon={FileText} title="Prep Report" subtitle="Department prep list, excess alerts & printing" onClick={() => navigate("/prep-list")} testid="shortcut-prep" />
      </div>
    </div>
  );
}
